import React from 'react';
import { createPortal } from 'react-dom';
import { formatCurrency } from '@koob/utils';
import AdditionalServicesBand, {
  AdditionalServicesBandScopeEnum,
  AdditionalServicesBandStateEnum,
} from '../AdditionalServicesBand';
import type { Translate } from './types';

interface TripDayExperienceGlobalBandProps {
  experience: any;
  globalExtrasCount: number;
  globalAvailableCount: number;
  globalSelectedExtras: any[];
  sumExtrasPrice: (extras: any[]) => number;
  extrasCurrency: (extras: any[]) => string;
  extrasAmountPerPax: (extras: any[]) => number | null;
  renderServiceChips: (extras: any[], scope: any) => React.ReactNode;
  // Absent on a read-only day: the band lists its services, nothing more.
  onAction?: () => void;
  globalServicesSlot?: any;
  t: Translate;
}

function getBandState(globalExtrasCount: number, globalAvailableCount: number) {
  if (globalExtrasCount > 0) return AdditionalServicesBandStateEnum.SELECTED;

  return globalAvailableCount > 0
    ? AdditionalServicesBandStateEnum.AVAILABLE
    : AdditionalServicesBandStateEnum.UNAVAILABLE;
}

// KD-3581 AC03 — a global service applies to the whole experience, so its
// band closes the frame after the last day instead of hanging off day 1.
// Only the DOM position moves: the counters, the chips and the modal stay on
// the day that owns the selection, and the band travels to the slot the
// frame lends through `globalServicesSlot`.
export default function TripDayExperienceGlobalBand({
  experience,
  globalExtrasCount,
  globalAvailableCount,
  globalSelectedExtras,
  sumExtrasPrice,
  extrasCurrency,
  extrasAmountPerPax,
  renderServiceChips,
  onAction,
  globalServicesSlot,
  t,
}: TripDayExperienceGlobalBandProps) {
  const perPaxAmount = extrasAmountPerPax(globalSelectedExtras);
  const isFirstDay =
    !!experience && (!experience?.dayIndex || experience.dayIndex === 1);

  if (!isFirstDay) {
    return null;
  }

  const globalServicesBand = (
    <AdditionalServicesBand
      scope={AdditionalServicesBandScopeEnum.GLOBAL}
      state={getBandState(globalExtrasCount, globalAvailableCount)}
      title={t('compose.services.globalTitle')}
      subtitle={
        globalExtrasCount > 0
          ? t('compose.services.globalSelected', {
              count: globalExtrasCount,
            })
          : t('compose.services.globalAvailable', {
              count: globalAvailableCount,
            })
      }
      emptyLabel={t('compose.services.globalEmpty')}
      totalLabel={t('compose.services.amount', {
        price: formatCurrency({
          amount: sumExtrasPrice(globalSelectedExtras),
          currency: extrasCurrency(globalSelectedExtras),
        }),
      })}
      perPaxLabel={
        perPaxAmount != null
          ? t('compose.services.perPax', {
              price: formatCurrency({
                amount: perPaxAmount,
                currency: extrasCurrency(globalSelectedExtras),
              }),
            })
          : undefined
      }
      actionLabel={
        onAction
          ? globalExtrasCount > 0
            ? t('compose.services.edit')
            : t('compose.services.add')
          : undefined
      }
      onAction={onAction}
    >
      {renderServiceChips(
        globalSelectedExtras,
        AdditionalServicesBandScopeEnum.GLOBAL,
      )}
    </AdditionalServicesBand>
  );

  if (globalServicesSlot) {
    return createPortal(globalServicesBand, globalServicesSlot);
  }

  // A frame is around this day but its slot is not attached yet: waiting one
  // commit beats showing the band in the wrong place. Outside any frame (no
  // prop at all) it stays where it is.
  return globalServicesSlot === undefined ? globalServicesBand : null;
}
